import { InvestmentObjectivesSetData, TransactionTypesEnum } from '@interfaces/models';
import { createSelector } from '@ngrx/store';
import { selectSetData } from '@store/selectors/investment-objectives/investment-objectives.selectors';
import { selectSettlementFundChoice } from '@store/selectors/open-account/open-account.selectors';

import * as MultiITSelectors from '../multi-internal-transfer.selectors';

// Per-Account OA Selectors (by account ID)

export const selectMultiITToByIdOpenAccount = (id: string) =>
  createSelector(MultiITSelectors.selectMultiITToById(id), (account) => account?.openAccount);

export const selectMultiITToByIdObjectives = (id: string) =>
  createSelector(selectMultiITToByIdOpenAccount(id), (oa) => oa?.objectives);

export const selectMultiITToByIdSettlementFund = (id: string) =>
  createSelector(selectMultiITToByIdOpenAccount(id), (oa) => oa?.settlementFund);

// Computed Selectors

export const selectOACompletedById = (id: string) =>
  createSelector(
    selectMultiITToByIdObjectives(id),
    selectMultiITToByIdSettlementFund(id),
    (objectives, settlementFund) => {
      return !!(objectives && settlementFund);
    },
  );

export const selectAllOACompleted = createSelector(
  MultiITSelectors.selectAllMultiITToAccounts,
  (accounts) =>
    accounts.every((account) => {
      const oa = account.openAccount;
      return !!(oa?.objectives && oa?.settlementFund);
    }),
);

// OA Collection State Selectors
export const selectOACollectionState = createSelector(
  MultiITSelectors.selectMultiITState,
  (state) => state.oaCollection,
);

export const selectCurrentlyCollectingAccountId = createSelector(
  selectOACollectionState,
  (collection) => collection?.currentlyCollecting ?? null,
);

export const selectCompletedNewAccountIds = createSelector(
  selectOACollectionState,
  (collection) => collection?.completedAccounts ?? [],
);

export const selectHasCompletedAccounts = createSelector(
  selectCompletedNewAccountIds,
  (completedIds) => completedIds.length > 0,
);

export const selectNewAccountRecipients = createSelector(
  MultiITSelectors.selectAllMultiITToAccounts,
  (accounts) => accounts.filter((a) => a.transactionType === TransactionTypesEnum.NEW_ACCOUNT),
);

export const selectAllNewAccountsComplete = createSelector(
  selectNewAccountRecipients,
  selectCompletedNewAccountIds,
  (newAccounts, completedIds) =>
    newAccounts.length > 0 && newAccounts.every((a) => completedIds.includes(a.transactionId)),
);

// Flow-aware selectors: return ITM per-entity data when collecting, otherwise global state
export const selectSettlementFundForCurrentFlow = createSelector(
  selectCurrentlyCollectingAccountId,
  MultiITSelectors.selectMultiITToEntities,
  selectSettlementFundChoice,
  (collectingId, entities, globalChoice) => {
    if (collectingId) {
      return entities[collectingId]?.openAccount?.settlementFund ?? null;
    }
    return globalChoice;
  },
);

export const selectInvestmentObjectivesForCurrentFlow = createSelector(
  selectCurrentlyCollectingAccountId,
  MultiITSelectors.selectMultiITToEntities,
  selectSetData,
  (collectingId, entities, globalSetData): InvestmentObjectivesSetData => {
    if (collectingId) {
      const perEntityObjectives = entities[collectingId]?.openAccount?.objectives;
      return {
        ...globalSetData,
        runValidation: false,
        selectedObjectives: perEntityObjectives?.selectedObjectives ?? [],
      };
    }
    return globalSetData;
  },
);

export const selectCurrentCollectingAccountNumber = createSelector(
  selectNewAccountRecipients,
  selectCurrentlyCollectingAccountId,
  (newAccounts, currentId): number | null => {
    if (!currentId) return null;
    const index = newAccounts.findIndex((a) => a.transactionId === currentId);
    return index >= 0 ? index + 1 : null;
  },
);
